import { getValue } from './get-value'
import { set, merge } from 'lodash-es'
import { toRegex } from './to-regex'
import { logger } from './logger'

/**
 * Given a URL, parse the query string to generate a settings object
 * If `settings` is passed, then the settings from the URL will be
 * merged with those `settings`. The settings from the URL will take
 * precedence over the passed `settings`.
 *
 * @param url the url from which to get settings
 * @param settings an existing settings object to merge new settings with
 * @param prefix when provided, only process query params that have this prefix
 */
export function getSettingsFromUrl<T> (url: string | URL, settings?: T, prefix?: string | RegExp): T {
  const parsedUrl = new URL(url)
  const params = parsedUrl.searchParams
  const urlSettings: Partial<T> = {}
  const prefixRegex = toRegex(prefix)
  params.forEach((value, key) => {
    const targetKey = getKey(key, prefixRegex)
    if (targetKey == null) return
    set(urlSettings, targetKey, getValue(value))
  })

  const pathConfig = getPathConfig(parsedUrl.pathname)

  if (pathConfig?.c) {
    set(urlSettings, 'config', getValue(pathConfig.c))
  }

  logger.debug('Loaded settings from URL', urlSettings)

  // if no settings were provided to merge with, then just return the new url settings
  if (settings == null) {
    return urlSettings as T
  }

  // generate the merged settings
  const mergedSettings = merge({}, settings, urlSettings)
  return mergedSettings
}

function getKey (key: string, prefix?: RegExp): string | null {
  if (prefix == null) return key
  if (!prefix.test(key)) return null
  return key.replace(prefix, '')
}

/**
 * Given a path, parse the path to generate a settings object
 *
 * For example: /c/SPBfacv4YKU-0d3JR0KUx => { c: 'SPBfacv4YKU-0d3JR0KUx' }
 *
 * @param path
 */
function getPathConfig (path: string): Record<string, string> | undefined {
  if (!path) return

  // this is super simple and only returns the two first segments of the path
  const [key, value] = path.split('/').filter(Boolean)

  return { [key]: value }
}
